import { MenubarLayout, Menubar } from '@criteria-labs/react'
import { Dialog, Transition } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { Fragment, ReactNode, useState } from 'react'
import { SidebarLayout } from '../../components/sidebar'
import { AboutMenu } from '../about-menu/AboutMenu'
import { EditMenu } from '../edit-menu/EditMenu'
import { FileMenu } from '../file-menu/FileMenu'
import { ProjectNavigator } from '../navigator/ProjectNavigator'
import { RunMenu } from '../run-menu/RunMenu'

export function ProjectLayout({
  documentMode,
  setDocumentMode,
  children
}: {
  documentMode: 'code' | 'inspector'
  setDocumentMode: (documentMode: 'code' | 'inspector') => void
  children?: ReactNode
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  return (
    <>
      <MenubarLayout
        menubar={
          <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-900">
            <div className="flex items-center px-2">
              <button
                type="button"
                className={clsx(
                  'my-0.5 rounded px-3 py-1.5 text-xs hover:bg-zinc-200 disabled:text-zinc-500 disabled:hover:bg-transparent lg:hidden dark:hover:bg-zinc-800'
                )}
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open navigator</span>
                <Bars3Icon className="h-4 w-4" aria-hidden="true" />
              </button>
              <Menubar aria-label="Project Menubar">
                <FileMenu />
                <EditMenu />
                <RunMenu />
                <AboutMenu />
              </Menubar>
            </div>
            <div className="px-2">
              <div className="my-0.5 flex items-center gap-1 p-0.5 text-xs">
                <button
                  type="button"
                  className={clsx(
                    'rounded px-2 py-1',
                    documentMode === 'code'
                      ? 'bg-zinc-300 dark:bg-zinc-700'
                      : 'hover:bg-zinc-200 dark:hover:bg-zinc-800'
                  )}
                  onClick={() => setDocumentMode('code')}
                >
                  Code
                  {/* <CodeBracketIcon className="size-4"  /> */}
                </button>
                <button
                  type="button"
                  className={clsx(
                    'rounded px-2 py-1',
                    documentMode === 'inspector'
                      ? 'bg-zinc-300 dark:bg-zinc-700'
                      : 'hover:bg-zinc-200 dark:hover:bg-zinc-800'
                  )}
                  onClick={() => setDocumentMode('inspector')}
                >
                  Inspector
                  {/* <TableCellsIcon className="size-4"  />*/}
                </button>
              </div>
            </div>
          </div>
        }
      >
        <SidebarLayout sidebar={<ProjectNavigator />}>{children}</SidebarLayout>
      </MenubarLayout>
      <Transition.Root as={Fragment} show={sidebarOpen}>
        <Dialog onClose={setSidebarOpen} className="relative lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-zinc-900/80" />
          </Transition.Child>
          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative w-64 shadow-lg">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close navigator</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>
                <ProjectNavigator />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}
